import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Card, DataTable, FilterBar, Modal, ConfirmDialog, FormField, TextInput, Select,
  EmptyState, type DataTableColumn,
} from '@/components/ui';
import { SectionToolbar } from './telephony/SectionToolbar';
import { adminApi, type BlockedNumber } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

export function BlockedNumbers({ tabs }: { tabs?: ReactNode } = {}) {
  const [rows, setRows] = useState<BlockedNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BlockedNumber | null>(null);

  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await adminApi.blockedNumbers({ search: search || undefined, per_page: 100 });
      if (seq !== reqSeq.current) return;
      setRows(res.data);
    } catch {
      if (seq === reqSeq.current) toast('שגיאה בטעינת המספרים החסומים', 'danger');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const h = setTimeout(() => void load(), 250);
    return () => clearTimeout(h);
  }, [load]);

  const columns: DataTableColumn<BlockedNumber>[] = useMemo(() => [
    { key: 'number', he: 'מספר', render: (r) => <strong dir="ltr">{r.display || r.number}</strong> },
    { key: 'reason', he: 'סיבה', render: (r) => r.reason || <span style={{ color: 'var(--muted)' }}>—</span> },
    {
      key: 'created_at', he: 'נחסם', align: 'start',
      render: (r) => <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.created_at ?? '—'}</span>,
    },
    {
      key: 'actions', he: '', align: 'center',
      render: (r) => (
        <button type="button" className="btn danger" style={{ height: 30 }} onClick={() => setDeleteTarget(r)}>
          הסר חסימה
        </button>
      ),
    },
  ], []);

  async function handleDelete(bn: BlockedNumber) {
    try {
      await adminApi.deleteBlockedNumber(bn.id);
      toast('החסימה הוסרה', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== bn.id));
    } catch {
      toast('שגיאה בהסרת החסימה', 'danger');
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <SectionToolbar
        tabs={tabs}
        title="מספרים חסומים"
        subtitle="מספר חסום — לא ניתן לחייג אליו והוא לא יכול לחייג אלינו"
        actions={
          <button type="button" className="btn primary" onClick={() => setAddOpen(true)}>+ הוסף מספר</button>
        }
      />
      <FilterBar search={search} onSearch={setSearch} onClear={() => setSearch('')} placeholder="חיפוש לפי מספר טלפון" />
      <Card padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          caption="מספרים חסומים"
          emptyState={
            <EmptyState
              icon="lock"
              title="אין מספרים חסומים"
              description="הוסף מספר כדי לחסום שיחות נכנסות ויוצאות מולו."
              action={<button type="button" className="btn primary" onClick={() => setAddOpen(true)}>+ הוסף מספר</button>}
            />
          }
        />
      </Card>

      {addOpen && <AddBlockedForm onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); void load(); }} />}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="הסרת חסימה"
        message={deleteTarget ? `להסיר את החסימה על ${deleteTarget.display || deleteTarget.number}? שיחות מולו יתאפשרו שוב.` : ''}
        confirmLabel="הסר חסימה"
      />
    </div>
  );
}

/** אפיון 6.8: סיבה היא חובה, ולחסימה אפשר לצרף סטטוס יעד לליד. */
const MIN_REASON_LENGTH = 5;
const TARGET_STATUS_OPTIONS = [
  { value: '', label: 'ללא שינוי סטטוס' },
  { value: 'dnc', label: 'לא ליצור קשר (DNC)' },
  { value: 'failed', label: 'נכשל' },
  { value: 'completed', label: 'הושלם' },
];

function AddBlockedForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [number, setNumber] = useState('');
  const [reason, setReason] = useState('');
  const [targetStatus, setTargetStatus] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!number.trim()) { toast('יש למלא מספר', 'danger'); return; }
    // נבדק כאן כדי שהמשתמש יקבל הסבר במקום 422 מהשרת.
    if (reason.trim().length < MIN_REASON_LENGTH) {
      toast(`יש לציין סיבה לחסימה — לפחות ${MIN_REASON_LENGTH} תווים`, 'danger');
      return;
    }
    setSaving(true);
    try {
      await adminApi.addBlockedNumber({
        number: number.trim(),
        reason: reason.trim(),
        target_status: targetStatus || null,
      });
      toast('המספר נחסם', 'ok');
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בחסימת המספר';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="חסימת מספר"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'חסום'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="מספר טלפון" required>
          <TextInput value={number} onChange={(e) => setNumber(e.target.value)} dir="ltr" placeholder="0502298318" autoFocus />
        </FormField>
        <FormField label="סיבה" required hint={`לפחות ${MIN_REASON_LENGTH} תווים — נשמר לתיעוד ומוצג ברשימה`}>
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="למשל: הטרדות חוזרות" />
        </FormField>
        <FormField label="סטטוס יעד לליד" hint="הלידים של המספר יעברו לסטטוס הזה. ניתן להשאיר ללא שינוי.">
          <Select value={targetStatus} onChange={setTargetStatus} options={TARGET_STATUS_OPTIONS} />
        </FormField>
      </div>
    </Modal>
  );
}
